import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, Clock, EuroIcon, FileSearch, MessageSquareText, Users, Zap, Check } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import T from "../components/EditableText";

const HERO_BG =
    "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzcxNTMzNTR8MA&ixlib=rb-4.1.0&q=85";

const PAINS = [
    { icon: Clock, title: "6+ Monate vakant", text: "Jeder Monat ohne Schlüsselrolle kostet Umsatz, Momentum und Team-Moral." },
    { icon: EuroIcon, title: "30–60% Jahresgehalt", text: "Ein Fehlhire kostet im Schnitt das 1.5-fache Jahresgehalt — plus Opportunitätskosten." },
    { icon: AlertTriangle, title: "70% Bewerber unpassend", text: "Klassische Stellenanzeigen filtern Top-Talente aktiv heraus, statt sie anzuziehen." },
];

const STAGES = [
    { n: "01", title: "Discovery", text: "Tiefen-Briefing: Rolle, Team, Kultur, must-haves und nice-to-haves. 90 Min." },
    { n: "02", title: "Search Strategy", text: "Mapping des Marktes: Wer wäre der ideale Kandidat? Wo finden wir ihn?" },
    { n: "03", title: "Direct Approach", text: "Personalisierte Ansprache von 50–80 passiven Kandidaten. Pro Woche." },
    { n: "04", title: "Assessment", text: "Strukturierte Interviews, Reference Checks, Cultural Fit. Wir bringen 3–5 Top-Kandidaten." },
    { n: "05", title: "Closing", text: "Vertragsverhandlung, Onboarding-Support, 6 Monate Garantie." },
];

const PRICING = [
    {
        name: "Contingency",
        price: "20%",
        period: "vom Jahresgehalt",
        text: "Erfolgsbasiert. Nur bei Hire.",
        features: ["Mid-Level Rollen", "12 Wochen Search", "3 Monate Garantie", "Mind. 5 Kandidaten"],
        cta: "Anfragen",
        popular: false,
    },
    {
        name: "Retained",
        price: "28%",
        period: "vom Jahresgehalt",
        text: "Standard für strategische Rollen.",
        features: [
            "Senior bis Lead-Rollen",
            "8 Wochen Search",
            "6 Monate Garantie",
            "Dedicated Researcher",
            "Wöchentliche Updates",
        ],
        cta: "Beratung anfragen",
        popular: true,
    },
    {
        name: "Executive",
        price: "33%",
        period: "vom Jahresgehalt",
        text: "C-Level & VP-Rollen.",
        features: [
            "Vollständig diskret",
            "12 Wochen Search",
            "12 Monate Garantie",
            "Partner-led Search",
            "Onboarding-Coaching",
        ],
        cta: "Vertraulich anfragen",
        popular: false,
    },
];

const FAQS = [
    {
        q: "Wie lange dauert eine durchschnittliche Besetzung?",
        a: "Für Mid-Level- und Senior-Rollen liegen wir bei 6–10 Wochen vom Briefing bis zum Vertragsabschluss. Executive Search benötigt 10–14 Wochen.",
    },
    {
        q: "Was passiert, wenn ein Kandidat nicht bleibt?",
        a: "Wir bieten je nach Paket 3, 6 oder 12 Monate Garantie. Sollte ein Kandidat in dieser Zeit ausscheiden, suchen wir kostenfrei nach.",
    },
    {
        q: "Arbeiten Sie auch mit Startups?",
        a: "Ja. Über 70% unserer Kunden sind Series-A bis Series-D Scale-ups. Wir verstehen die Dynamik und Geschwindigkeit.",
    },
    {
        q: "Wie unterscheiden Sie sich von klassischen Personalberatungen?",
        a: "Wir sind kleiner, schneller und spezialisierter. Keine durchgereichten CVs, keine 100-Kandidaten-Pipelines. Jede Rolle bekommt einen dedizierten Researcher und Partner.",
    },
    {
        q: "Wie funktioniert das Sourcing?",
        a: "Wir kombinieren AI-gestützte Datenanalyse (LinkedIn, GitHub, Talent-Pools) mit klassischer Recherche und unserem Netzwerk. 80% der Hires sind passive Kandidaten.",
    },
];

export default function Unternehmen() {
    return (
        <div data-testid="unternehmen-page">
            {/* HERO */}
            <section className="relative bg-aimploy-navy text-white overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-r from-aimploy-navy via-aimploy-navy/95 to-aimploy-navy/70" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs uppercase tracking-[0.2em] font-semibold backdrop-blur-md">
                            <T k="unternehmen.hero.badge">Für Hiring-Manager</T>
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mt-8">
                            <T k="unternehmen.hero.line1">Schluss mit</T>
                            <br />
                            <T k="unternehmen.hero.line2" className="text-aimploy-violet italic font-medium">"Wir suchen seit 6 Monaten."</T>
                        </h1>
                        <T k="unternehmen.hero.subtitle" as="p" className="text-lg lg:text-xl text-white/70 max-w-2xl mt-8 leading-relaxed">
                            aimploy ist Ihr Spezialist-Team für Senior-, Lead- und Executive-Rollen. Strukturiert, schnell, mit echter Garantie auf den Match.
                        </T>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <Link
                                to="/kontakt"
                                data-testid="unternehmen-hero-cta"
                                className="inline-flex items-center justify-center gap-2 bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-[1.02]"
                            >
                                <T k="unternehmen.hero.cta_primary">Kostenloses Erstgespräch</T>
                                <ArrowUpRight className="w-5 h-5" />
                            </Link>
                            <a
                                href="#prozess"
                                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 px-8 py-4 rounded-full text-base font-semibold backdrop-blur-md"
                            >
                                <T k="unternehmen.hero.cta_secondary">Unser Prozess</T>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* PAIN POINTS */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="unt.pains.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">
                            Was kostet schlechtes Recruiting?
                        </T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="unt.pains.title">{`Drei stille Killer\njedes Wachstumsplans.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PAINS.map((p, i) => (
                            <div
                                key={p.title}
                                className="bg-white rounded-2xl p-8 lg:p-10 border border-black/5 hover:-translate-y-1 transition-all"
                            >
                                <div className="h-12 w-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center mb-8">
                                    <p.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display text-2xl font-bold mb-3"><T k={`unt.pain.${i}.title`}>{p.title}</T></h3>
                                <T k={`unt.pain.${i}.text`} as="p" className="text-aimploy-muted">{p.text}</T>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section id="prozess" className="py-24 lg:py-32 bg-aimploy-dark text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dots-dark opacity-30" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-20">
                        <T k="unt.process.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Prozess</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="unt.process.title">{`5 Stages.\nVom Briefing zum Hire.`}</T>
                        </h2>
                    </div>
                    <div className="space-y-4">
                        {STAGES.map((s, i) => (
                            <div
                                key={s.n}
                                className="group flex flex-col md:flex-row gap-8 items-start md:items-center bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl p-8 lg:p-10 transition-all"
                            >
                                <div className="font-display text-6xl lg:text-7xl font-bold text-aimploy-violet/30 group-hover:text-aimploy-violet transition-colors">
                                    {s.n}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-display text-2xl lg:text-3xl font-bold mb-2"><T k={`unt.stage.${i}.title`}>{s.title}</T></h3>
                                    <T k={`unt.stage.${i}.text`} as="p" className="text-white/60 leading-relaxed max-w-2xl">{s.text}</T>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="unt.serv.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Leistungen</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="unt.serv.title">{`Drei Wege, mit uns\nzu arbeiten.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { icon: FileSearch, title: "Executive Search", text: "Diskret. Partner-led. Für C-Level- und VP-Rollen." },
                            { icon: Zap, title: "Contingency", text: "Erfolgsbasiert. Schnell. Für Mid-Level bis Senior." },
                            { icon: Users, title: "Retained Search", text: "Strategisch. Exklusiv. Für strategisch kritische Rollen." },
                        ].map((s, i) => (
                            <div key={s.title} className="bg-white rounded-2xl p-8 lg:p-10 border border-black/5 hover:-translate-y-1 transition-all">
                                <div className="h-12 w-12 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center mb-8">
                                    <s.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display text-2xl font-bold mb-3"><T k={`unt.serv.${i}.title`}>{s.title}</T></h3>
                                <T k={`unt.serv.${i}.text`} as="p" className="text-aimploy-muted">{s.text}</T>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <T k="unt.price.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Pricing</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="unt.price.title">{`Transparente Preise.\nKeine versteckten Kosten.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PRICING.map((p, i) => (
                            <div
                                key={p.name}
                                data-testid={`pricing-${p.name.toLowerCase()}`}
                                className={`relative rounded-2xl p-8 lg:p-10 transition-all hover:-translate-y-1 ${
                                    p.popular
                                        ? "bg-aimploy-navy text-white border-2 border-aimploy-violet shadow-[0_20px_60px_rgba(108,99,255,0.25)]"
                                        : "bg-aimploy-bg text-aimploy-navy border border-black/5"
                                }`}
                            >
                                {p.popular && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-aimploy-violet text-white text-xs uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-full">
                                        <T k="unt.price.popular_badge">Beliebt</T>
                                    </span>
                                )}
                                <h3 className="font-display text-2xl font-bold"><T k={`unt.price.${i}.name`}>{p.name}</T></h3>
                                <div className="mt-6 mb-2">
                                    <span className="font-display text-5xl lg:text-6xl font-bold"><T k={`unt.price.${i}.price`}>{p.price}</T></span>
                                </div>
                                <T k={`unt.price.${i}.period`} as="p" className={`text-sm ${p.popular ? "text-white/60" : "text-aimploy-muted"}`}>{p.period}</T>
                                <T k={`unt.price.${i}.text`} as="p" className={`mt-4 ${p.popular ? "text-white/80" : "text-aimploy-muted"}`}>{p.text}</T>
                                <ul className="mt-8 space-y-3 text-sm">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex gap-3">
                                            <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${p.popular ? "text-aimploy-violet" : "text-aimploy-violet"}`} />
                                            <T k={`unt.price.${i}.feature.${p.features.indexOf(f)}`}>{f}</T>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/kontakt"
                                    className={`mt-10 block w-full text-center px-6 py-3.5 rounded-xl text-sm font-semibold transition-colors ${
                                        p.popular
                                            ? "bg-aimploy-violet hover:bg-aimploy-violet-hover text-white"
                                            : "bg-aimploy-navy hover:bg-aimploy-navy-hover text-white"
                                    }`}
                                >
                                    <T k={`unt.price.${i}.cta`}>{p.cta}</T>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[900px] mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <T k="unt.faq.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">FAQ</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="unt.faq.title">Häufige Fragen.</T>
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-3" data-testid="unternehmen-faq">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="bg-white rounded-2xl border border-black/5 px-6 [&[data-state=open]]:border-aimploy-violet/30"
                            >
                                <AccordionTrigger className="text-left font-display text-lg font-bold py-6 hover:no-underline">
                                    <T k={`unt.faq.${i}.q`} as="span">{f.q}</T>
                                </AccordionTrigger>
                                <AccordionContent className="text-aimploy-muted pb-6 leading-relaxed">
                                    <T k={`unt.faq.${i}.a`}>{f.a}</T>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
                    <MessageSquareText className="w-12 h-12 text-aimploy-violet mx-auto mb-6" />
                    <h2 className="font-display text-5xl lg:text-7xl font-bold leading-[0.95] max-w-4xl mx-auto">
                        <T k="unt.cta.title">{`30 Min.\nkostenloses Erstgespräch.`}</T>
                    </h2>
                    <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 mt-10 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-10 py-5 rounded-full text-base font-semibold hover:scale-[1.02] transition-all"
                    >
                        <T k="unt.cta.button">Termin buchen</T> <ArrowUpRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
